import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSEO, breadcrumbLd } from "@/lib/seo";
import BookButton from "@/components/BookButton";

export default function Blog() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  useSEO({
    title: "The Local Journal — Travel, Highway & Carrollton Notes",
    description:
      "Practical, hyper-local writing from Westbridge Inn & Suites: where to stay between Kansas City and Chillicothe, trucker overnight tips on US-65, pet-friendly travel in Carroll County, and more.",
    path: "/blog",
    jsonLd: breadcrumbLd([
      { name: "Home", path: "/" },
      { name: "Journal", path: "/blog" },
    ]),
  });

  return (
    <div data-testid="blog-page">
      <section className="pt-36 pb-12 bg-canvas">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="font-mono-label text-ember mb-4">The Journal · Carrollton, Missouri</p>
            <h1 className="font-display text-6xl md:text-7xl text-ink leading-[0.98]">
              Notes from<br /><span className="italic">the front desk.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-ink-soft leading-relaxed">
              Honest, hyper-local writing about Carroll County, the US-65 corridor and the small towns
              around us. No SEO fluff, no AI slurry — written by people who actually live here.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-canvas pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-px bg-rule-strong">
          {posts.map((p, i) => (
            <Link
              key={p.id}
              to={`/blog/${p.slug}`}
              className={`bg-canvas overflow-hidden group fade-up ${i === 0 ? "md:col-span-8 md:row-span-2" : "md:col-span-4"}`}
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`blog-card-${p.slug}`}
            >
              <div className={`overflow-hidden bg-dune ${i === 0 ? "aspect-[16/10]" : "aspect-[16/11]"}`}>
                <img src={p.hero_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" loading="lazy" />
              </div>
              <div className="p-8 md:p-10">
                <p className="font-mono-label text-ember mb-3">
                  {p.category} · {new Date(p.published_at).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                </p>
                <h2 className={`font-display text-ink leading-tight ${i === 0 ? "text-4xl md:text-5xl" : "text-2xl"}`}>
                  {p.title}
                </h2>
                <p className="mt-4 text-ink-soft leading-relaxed line-clamp-3">{p.excerpt}</p>
                <p className="mt-4 font-mono-label text-ink-muted link-underline inline-block">Read article →</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-ember py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-wrap items-center justify-between gap-6">
          <h2 className="font-display text-3xl md:text-5xl text-canvas leading-tight max-w-2xl">
            Ready to plan your stay in Carrollton?
          </h2>
          <BookButton label="Book Direct & Save" variant="ink" className="!border-canvas !text-canvas hover:!bg-canvas hover:!text-ember" testid="blog-cta-book" />
        </div>
      </section>
    </div>
  );
}
